package diana.soleil.design.iraniancanadienbazzar.notifications

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * Hand-off of a tapped system notification's routing data from the platform entry point into shared
 * navigation. Platforms [offer] the FCM data payload (Android: launcher-intent extras; iOS: the
 * `userInfo` of the tapped notification once APNs is wired); `AppNavHost` observes [pending] and
 * navigates, then [consume]s. A process-wide object (not DI) because the offer side runs before the
 * composition — and Koin — exist on a cold start.
 *
 * Payload keys mirror what `firebase/functions/index.js` sends with every push: `type`
 * (`message` | `review_reply` | `comment` | `order` | `event`) plus `conversationId` /
 * `businessId` / `postId` / `orderId` / `eventId`.
 *
 * The same bus also carries **HTTPS App/Universal Links** on our own domain ([offerUrl]) — a tapped
 * `mahaleh.ca/b/…`, `/item/…`, `/post/…`, `/event/…` or `/gems/{section}/…` link opens the matching
 * screen instead of the browser (Android autoVerify intent-filter → [MainActivity], iOS `onOpenURL`,
 * verified by the `.well-known/assetlinks.json` + `apple-app-site-association` served from Hosting).
 */
object NotificationDeepLinks {

    sealed interface Target {
        data class Conversation(val conversationId: String) : Target
        data class Business(val businessId: String) : Target
        data class Post(val postId: String) : Target
        data class MarketplaceItem(val itemId: String) : Target
        data class Event(val eventId: String) : Target
        data class Channel(val businessId: String) : Target
        data class News(val articleId: String) : Target
        data object Orders : Target

        /**
         * A Hidden Gems push. [kind] is joke/fact/recipe/tale; [gemId] only opens a detail screen
         * for recipes and tales — jokes and facts have list surfaces only, so they land there.
         */
        data class Gem(val kind: String, val gemId: String) : Target
    }

    private val _pending = MutableStateFlow<Target?>(null)
    val pending: StateFlow<Target?> = _pending

    /** Parses an FCM data payload; unknown/incomplete payloads are ignored. */
    fun offer(data: Map<String, String?>) {
        val target = when (data["type"]) {
            "message" -> data["conversationId"]?.takeIf { it.isNotBlank() }?.let { Target.Conversation(it) }
            "review_reply" -> data["businessId"]?.takeIf { it.isNotBlank() }?.let { Target.Business(it) }
            "comment" -> data["postId"]?.takeIf { it.isNotBlank() }?.let { Target.Post(it) }
            "order" -> Target.Orders
            "event" -> data["eventId"]?.takeIf { it.isNotBlank() }?.let { Target.Event(it) }
            "channel" -> data["businessId"]?.takeIf { it.isNotBlank() }?.let { Target.Channel(it) }
            // Admin campaigns carry a full universal-link URL — reuse the share-link parser so a
            // campaign can target any surface those links support (b/item/post/event).
            "gem" -> data["gemKind"]?.takeIf { it.isNotBlank() }
                ?.let { Target.Gem(it, data["gemId"].orEmpty()) }
            "campaign" -> data["link"]?.let(::parseUrl)
            else -> null
        }
        if (target != null) _pending.value = target
    }

    /**
     * Parses a tapped share link (`https://mahaleh.ca/{b|item|post|event}/{id}`) into a [Target].
     * Matches the paths [ShareConfig] emits and the Hosting rewrites. Query/fragment are stripped.
     * Returns false for a link that doesn't match a known `{prefix}/{id}` shape, so the caller can
     * hand it to the browser instead of silently dropping the tap.
     */
    fun offerUrl(url: String?): Boolean {
        val target = url?.let(::parseUrl) ?: return false
        _pending.value = target
        return true
    }

    internal fun parseUrl(url: String): Target? {
        // Drop scheme+host and any ?query / #fragment, then split into path segments.
        val afterScheme = url.substringAfter("://", url)
        val path = afterScheme.substringAfter('/', "").substringBefore('?').substringBefore('#')
        val segments = path.split('/').filter { it.isNotBlank() }
        if (segments.size < 2) return null
        val id = segments[1]
        return when (segments[0]) {
            "b" -> Target.Business(id)
            "item" -> Target.MarketplaceItem(id)
            "post" -> Target.Post(id)
            "event" -> Target.Event(id)
            // Web nav writes /news/<id> URLs (WebNavHistory pathFor) — parse them back on refresh.
            "news" -> Target.News(id)
            // Hidden Gems: `/gems/{section}/{id}`, so the id is one segment further along.
            "gems" -> segments.getOrNull(2)
                ?.let { gemId -> gemKindForPath(id)?.let { Target.Gem(it, gemId) } }
            else -> null
        }
    }

    /**
     * Maps the `/gems/{section}/` path [ShareConfig] mints to the `kind` the push payload and
     * `gemRouteFor` already speak. The two vocabularies differ for two sections — the URLs say
     * `danestaniha`/`shahnameh` (what the reader calls them) while the routing says `fact`/`tale` —
     * so translate here rather than teaching either side the other's names. An unknown section
     * returns null so the link opens in the browser instead of landing on the wrong screen.
     */
    private fun gemKindForPath(section: String): String? = when (section) {
        "joke" -> "joke"
        "recipe" -> "recipe"
        "danestaniha" -> "fact"
        "shahnameh" -> "tale"
        "poem" -> "poem"
        "story" -> "story"
        else -> null
    }

    fun consume() {
        _pending.value = null
    }
}
